import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { IdName } from '@shared/models/id-name';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { filter, map, shareReplay, tap } from 'rxjs/operators';
import { RxRulesService } from '@shared/services/rx-rules-helper/rx-rules.service';
import { RxShadeSystemRulesService } from '@shared/services/rx-rules-helper/rx-shade-system-rules.service';
import { ShadeSystem } from '@shared/models/shade-system';
import { DentureDetailsState, DentureDetailsStore } from '@modules/denture-details/state/denture-details.store';
import { DentureDetailsQuery } from '@modules/denture-details/state/denture-details.query';
import { combineQueries } from '@datorama/akita';
import { OrderQuery } from '@modules/order/state/order-query';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { RxConfiguration } from '@shared/models/rx-configuration';
import { DentureDetailsModel } from '@shared/models/rx-models/interfaces/denture-details-model';
import type { Maybe } from '@/types/globals';

@Injectable()
export class DentureDetailsFacade {
	stages$: Observable<IdName[]> = this.shellQuery.rxConfiguration$.pipe(map(c => c?.Stages ?? []));
	moulds$: Observable<IdName[]> = this.shellQuery.rxConfiguration$.pipe(map(c => c?.Moulds ?? []));

	shadeSystems: ShadeSystem[];

	teethShades$: Observable<string[] | undefined> = this.query.shadeSystem$.pipe(
		map(s => s?.Options),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	gingivalShades$: Observable<IdName[]> = this.shellQuery.rxConfiguration$.pipe(
		filter((c): c is RxConfiguration => !!c),
		map(c => c.DentureGingivalShades)
	);

	shouldTeethShadeBeSelected$: Observable<boolean> = this.teethShades$.pipe(
		map(x => DentureDetailsFacade.shouldTeethShadeBeSelected(x)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	shouldValidateForSend$: Observable<boolean> = this.shellQuery.shouldValidateForSend$.pipe(
		shareReplay({ bufferSize: 1, refCount: true })
	);
	isReadonly$ = this.shellQuery.getReadOnlyState('dentureDetails').pipe(shareReplay({ bufferSize: 1, refCount: true }));

	dentureUpperSectionIsReadonly$ = this.orderQuery.dentureUpperSectionIsReadonly$;
	dentureLowerSectionIsReadonly$ = this.orderQuery.dentureLowerSectionIsReadonly$;
	checkIsToothDiagramEnabled$ = this.orderQuery.procedureMap$.pipe(map(procedureMap => procedureMap?.ToothDiagramIsEnabled ?? false));

	getUpperSectionToggleValueWhenToothSelected$ = combineLatest([this.teethDiagramQuery.upperJaw$, this.orderQuery.procedureMap$]).pipe(
		map(([teeth, procedureMap]) => this.rxRulesService.checkDentureDetailsRule(teeth, procedureMap?.TypeId ?? 0))
	);

	getLowerSectionToggleValueWhenToothSelected$ = combineLatest([this.teethDiagramQuery.lowerJaw$, this.orderQuery.procedureMap$]).pipe(
		map(([teeth, procedureMap]) => this.rxRulesService.checkDentureDetailsRule(teeth, procedureMap?.TypeId ?? 0))
	);

	rxConfiguration$ = this.shellQuery.rxConfiguration$;

	dentureCopyScanValue$ = this.query.isDentureCopyScan$;

	get storeState(): DentureDetailsState {
		return this.query.getValue();
	}

	constructor(
		private readonly store: DentureDetailsStore,
		private readonly query: DentureDetailsQuery,
		private readonly shellQuery: ShellQuery,
		private readonly rxRulesService: RxRulesService,
		private readonly rxShadeSystemRulesService: RxShadeSystemRulesService,
		private readonly orderQuery: OrderQuery,
		private readonly teethDiagramQuery: TeethDiagramQuery
	) {
		this.shadeSystems = this.rxShadeSystemRulesService.getDefaultShadeSystemOptions();
	}

	updateStore(formValues: DentureDetailsState): { formValues: DentureDetailsState; isFormValuesModified: boolean } {
		let isFormValuesModified = false;

		if (
			this.query.shadeSystem !== formValues.shadeSystem &&
			DentureDetailsFacade.shouldTeethShadeBeSelected(formValues.shadeSystem?.Options ?? []) &&
			!formValues.shadeSystem?.Options.includes(formValues.teethShade ?? '')
		) {
			formValues.teethShade = null;
			isFormValuesModified = true;
		}

		this.store.update(formValues);

		return { formValues, isFormValuesModified };
	}

	getLoadedRx(): Observable<DentureDetailsState> {
		return combineQueries([
			this.shellQuery.dentureDetails$,
			this.shellQuery.rxConfiguration$,
			this.shellQuery.isProcedureChanged$
		]).pipe(
			filter((latest): latest is [DentureDetailsModel, RxConfiguration, boolean] => {
				const [dentureDetails, rxConfiguration, isProcedureChanged] = latest;

				return !!dentureDetails && !!rxConfiguration && !isProcedureChanged;
			}),
			map(([dentureDetails, rxConfiguration]): DentureDetailsState => {
				return {
					isUpperJawChecked: dentureDetails.UpperJaw,
					isLowerJawChecked: dentureDetails.LowerJaw,
					stage: rxConfiguration.Stages.find(x => x.Id === dentureDetails.Stage) ?? null,
					mould: rxConfiguration.Moulds.find(x => x.Id === dentureDetails.Mould) ?? null,
					shadeSystem: this.shadeSystems.find(x => x.Id === dentureDetails.ShadeSystemId) ?? null,
					teethShade: dentureDetails.TeethShade,
					gingivalShade: rxConfiguration.DentureGingivalShades.find(x => x.Id === dentureDetails.GingivalShadeId) ?? null,
					isDentureCopyScan: dentureDetails.IsDentureCopyScan
				};
			}),
			tap(dentureDetails => {
				this.store.update(dentureDetails);
			})
		);
	}
	getSrcForRadioButtonImg(checked: boolean): string {
		return checked
			? `${this.shellQuery.staticFilesEndpoint}/assets/icons/check.svg`
			: `${this.shellQuery.staticFilesEndpoint}/assets/icons/uncheck.svg`;
	}

	getDefaultShadeSystem(): Observable<ShadeSystem | undefined> {
		return combineQueries([this.shellQuery.defaultShade$, this.shellQuery.isReadOnly$]).pipe(
			filter(([, isReadOnly]) => !isReadOnly),
			map(([shadeSystem]) => {
				return this.shadeSystems.find(x => x.Id === shadeSystem);
			})
		);
	}

	private static shouldTeethShadeBeSelected(teethShades: Maybe<string[]>): boolean {
		return !!teethShades?.length;
	}
}
